/*
 *
 * PlatePanel
 *
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectCanDeletePlate,
  makeSelectIsTopPlate,
  makeSelectIsVisiblePlatePanel,
} from './selectors';
import reducer from './reducer';
import { RootState } from './types';
import Panel from 'components/Panel';
import {
  displayPlatePanelAction,
  removePlateAction,
  updatePlateAction,
} from 'containers/PlatePanel/actions';
import { makeSelectShelvesSelected } from 'containers/Configurator/selectors/selection';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import PlateColorPicker from 'containers/PlatePanel/PlateColorPicker';
import {
  makeSelectContext,
  makeSelectIsVisibleColorsPanel,
} from 'containers/Configurator/selectors/general';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import RemoveButton from 'components/RemoveButton';
import { useInjectSaga } from 'utils/injectSaga';
import saga from './saga';
import useMedia from 'use-media';
import PanelSelection from 'components/PanelSelection';
import GenericObjectSelector, {
  SelectorSize,
} from 'components/GenericObjectSelector';
import {
  makeSelectCommonGlassPlate,
  makeSelectCommonPlateColors,
} from 'containers/Ribbon/ShelvesRibbon/ColorsSection/selectors';
import { ColorPreviewItem } from 'containers/PanesPanel/selectors';
import { useTranslation } from 'react-i18next';
import {
  makeSelectIsGlassTopPlate,
  makeSelectTopPlateColor,
} from 'containers/Ribbon/CabinetRibbon/selectors';
import { makeSelectIsLastShelvesSelected } from 'containers/Configurator/selectors/history';
import AllSelector from 'components/AllSelector';
import { PaneMaterial, WHITE_COLOR } from 'lib/modelBuilder/constants';
import { findColor } from 'lib/modelBuilder/utils/colorHelper';
import { Color } from 'lib/modelBuilder/entities/Color';
import { ActivePanel } from '../Configurator/constants/general';
import PanelSwitcher from '../PanelSwitcher';
import { FurnitureEditorContext } from '../../lib/constraintsChecker/FurnitureEditor';

interface Props {
  top?: boolean;
}

interface StateProps {
  isVisible: boolean;
  isVisiblePanel: boolean;
  canDelete: boolean;
  shelvesSelected: Shelf[];
  isTopPlate2: boolean;
  plateColor: ColorPreviewItem;
  topPlateColor: ColorPreviewItem;
  superiorPlateInSelectedShelves: boolean;
  context: FurnitureEditorContext;
  isGlassTopPlate: boolean;
  isGlassPlate: boolean;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectIsVisibleColorsPanel(ActivePanel.plates),
  isVisiblePanel: makeSelectIsVisiblePlatePanel(),
  canDelete: makeSelectCanDeletePlate(),
  shelvesSelected: makeSelectShelvesSelected(),
  isTopPlate2: makeSelectIsTopPlate(),
  plateColor: makeSelectCommonPlateColors(),
  topPlateColor: makeSelectTopPlateColor(),
  isGlassTopPlate: makeSelectIsGlassTopPlate(),
  isGlassPlate: makeSelectCommonGlassPlate(),
  superiorPlateInSelectedShelves: makeSelectIsLastShelvesSelected(),
  context: makeSelectContext(),
});

const key = 'platePanel';

function PlatePanel({ top = false }: Props) {
  useInjectReducer({ key: key, reducer: reducer });
  useInjectSaga({ key: key, saga: saga });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [shouldUpdateShelvesPlate, setShouldUpdateShelvesPlate] = useState(
    true,
  );
  const [shouldUpdateTopPlate, setShouldUpdateTopPlate] = useState(true);
  const {
    isVisible,
    isVisiblePanel,
    shelvesSelected,
    canDelete,
    isTopPlate2,
    plateColor,
    topPlateColor,
    superiorPlateInSelectedShelves,
    context,
    isGlassTopPlate,
    isGlassPlate,
  } = useSelector(stateSelector);
  const isMobile = useMedia({ maxWidth: 1023 });
  const isXLarge = useMedia({ minWidth: 1280 });
  const isTopPlate = top || isTopPlate2;

  useEffect(() => {
    if (!superiorPlateInSelectedShelves) {
      setShouldUpdateTopPlate(false);
      setShouldUpdateShelvesPlate(true);
    } else {
      setShouldUpdateTopPlate(true);
    }
  }, [superiorPlateInSelectedShelves]);

  const hidePanel = () => {
    if (isTopPlate && !isMobile) dispatch(displayPlatePanelAction(false));
    else dispatch(setColorsPanelAction());
    // dispatch(setShelfModeAction(true));
    setShouldUpdateShelvesPlate(true);
    setShouldUpdateTopPlate(true);
  };

  const remove = () => {
    dispatch(removePlateAction(isTopPlate));
    hidePanel();
  };

  const selectAllPlates = () => {
    setShouldUpdateShelvesPlate(true);
    setShouldUpdateTopPlate(true);
    // dispatch(setShelfModeAction(false));

    if (isMobile) {
      dispatch(setColorsPanelAction(ActivePanel.plates, false));
    }
  };

  const handleOnClickUpperPlate = () => {
    if (shouldUpdateShelvesPlate) {
      setShouldUpdateShelvesPlate(false);
    }
    setShouldUpdateTopPlate(true);
    // dispatch(setShelfModeAction(false));

    if (!topPlateColor) {
      dispatch(
        updatePlateAction(
          false,
          PaneMaterial.MDF,
          findColor(WHITE_COLOR),
          false,
          true,
          false,
        ),
      );
    }
  };

  const handleOnClickShelvesPlates = () => {
    if (shouldUpdateTopPlate) {
      setShouldUpdateTopPlate(false);
    }
    setShouldUpdateShelvesPlate(true);
    // dispatch(setShelfModeAction(true));

    if (!canDelete) {
      dispatch(
        updatePlateAction(
          false,
          PaneMaterial.MDF,
          findColor(WHITE_COLOR),
          false,
          false,
          true,
        ),
      );
    }
  };

  let commonColor: Color | undefined = plateColor;
  if (shouldUpdateTopPlate) {
    if (
      plateColor &&
      shouldUpdateShelvesPlate &&
      topPlateColor?.id !== plateColor?.id
    ) {
      commonColor = undefined;
    } else {
      commonColor = topPlateColor;
    }
  }

  if (isMobile) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center">
          <div className="mr-2">
            <GenericObjectSelector
              title={
                shelvesSelected.length > 1
                  ? t('Inner plates')
                  : t('Inner plate')
              }
              size={SelectorSize.large}
              color={plateColor}
              canAddNew={true}
              present={canDelete}
              selected={false}
              onClick={() => {
                handleOnClickShelvesPlates();
                dispatch(setColorsPanelAction(ActivePanel.plates, false));
              }}
              glass={isGlassPlate}
            />
          </div>

          {superiorPlateInSelectedShelves && (
            <GenericObjectSelector
              title={`Upper plate`}
              size={SelectorSize.large}
              color={topPlateColor}
              canAddNew={true}
              present={!!topPlateColor}
              selected={false}
              onClick={() => {
                handleOnClickUpperPlate();
                dispatch(setColorsPanelAction(ActivePanel.plates, false));
              }}
              glass={isGlassTopPlate}
            />
          )}
          {superiorPlateInSelectedShelves && (
            <AllSelector onClick={selectAllPlates} className="mx-2">
              {t('All')}
            </AllSelector>
          )}
        </div>
        <PanelSelection
          close={hidePanel}
          isVisible={isVisible || isVisiblePanel}
          autoclose={false}
          remove={canDelete && remove}
        >
          <div className="flex justify-center w-full mt-10">
            <div className="w-full">
              <PlateColorPicker
                updateShelvesPlates={shouldUpdateShelvesPlate}
                updateTopPlate={shouldUpdateTopPlate}
                commonColor={commonColor}
                hidePanel={hidePanel}
              />
            </div>
          </div>
        </PanelSelection>
      </div>
    );
  } else {
    return (
      <Panel
        close={hidePanel}
        isVisible={isVisible || isVisiblePanel}
        autoclose={false}
      >
        <div className="flex justify-center w-full">
          <div className="w-1/3 flex flex-col justify-center items-center">
            <div className="flex justify-center w-full">
              <PanelSwitcher />
            </div>
            <div className="flex justify-center items-center">
              {superiorPlateInSelectedShelves &&
                isXLarge &&
                context !== FurnitureEditorContext.webShop && (
                  <AllSelector onClick={selectAllPlates} className="mx-2">
                    {t('All')}
                  </AllSelector>
                )}
              <div className="mr-2">
                <GenericObjectSelector
                  title={
                    shelvesSelected.length > 1
                      ? t('Inner plates')
                      : t('Inner plate')
                  }
                  color={plateColor}
                  canAddNew={true}
                  present={canDelete}
                  selected={shouldUpdateShelvesPlate}
                  onClick={handleOnClickShelvesPlates}
                  size={SelectorSize.large}
                  glass={isGlassPlate}
                />
              </div>
              {superiorPlateInSelectedShelves && (
                <GenericObjectSelector
                  title={t('Upper plate')}
                  color={topPlateColor}
                  canAddNew={true}
                  present={!!topPlateColor}
                  selected={shouldUpdateTopPlate}
                  onClick={handleOnClickUpperPlate}
                  size={SelectorSize.large}
                  glass={isGlassTopPlate}
                />
              )}
            </div>
          </div>
          {canDelete && (
            <div className="lg:mt-10 lg:ml-4">
              <RemoveButton
                onClick={() => {
                  if (shouldUpdateTopPlate) {
                    dispatch(removePlateAction(true));
                  }
                  if (shouldUpdateShelvesPlate) {
                    dispatch(removePlateAction(false));
                  }
                }}
              />
            </div>
          )}
          <div className="w-2/3 flex mr-6">
            <div className="w-full">
              <PlateColorPicker
                updateShelvesPlates={shouldUpdateShelvesPlate}
                updateTopPlate={shouldUpdateTopPlate}
                commonColor={commonColor}
                hidePanel={hidePanel}
              />
            </div>
          </div>
        </div>
      </Panel>
    );
  }
}

export default PlatePanel;
